import { useMemo } from 'react';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import type { QuickSearchResultSection } from '../types';
import type { ListMode } from './useQuickSearchListMode';

/**
 * Hook to flatten search results for easier lookup operations.
 *
 * This hook memoizes the flattening operation to avoid recalculating on every render.
 * `SINGLE` and `MULTI_TABBED` already provide a flat array of results.
 * `MULTI` provides sections, so those items are flattened here.
 *
 * @param items - The search results in either flat or sectioned format
 * @param listMode - The current list display mode
 * @returns Flattened array of QuickSearchResult items
 */
export const useFlattenedSearchResults = (
  items: QuickSearchResult[] | QuickSearchResultSection[],
  listMode: ListMode
): QuickSearchResult[] => {
  return useMemo(() => {
    if (listMode === 'SINGLE' || listMode === 'MULTI_TABBED') {
      return items as QuickSearchResult[];
    }

    // MULTI mode: flatten sections
    return (items as QuickSearchResultSection[]).flatMap(
      (section) => section.items
    );
  }, [items, listMode]);
};
